import React from 'react';
import { Alert, Card, Space, Table, Tag, Typography } from 'antd';
import { Link as RouterLink } from 'react-router-dom';
import { classTagMap, classTooltipMap } from './triageMaps';
import { FLEET_CLASS_IDS } from '../types/triage';

const { Title, Paragraph, Text } = Typography;

const COLD_CLASS_IDS = new Set([57, 58, 60]);

const CLASS_PICK_WHEN: Record<number, string> = {
  5: 'Voltage decayed, no other failure signal. Plan a battery change.',
  10: "You honestly don't know yet. Better than a confident wrong guess.",
  54: 'Repeated failures, test-jig flagged it, repeat offender after replacement.',
  55: 'Past expected lifespan; not field-repairable.',
  56: 'Battery removed, physical interference, deliberate disconnect.',
  57: 'Failed OTA, stuck after firmware push, known firmware regression.',
  58: 'Node moved, position changed, signal-strength drift.',
  59: 'Remote signals inconclusive; needs eyes on site.',
  60: 'Sensor blip, noise, no real issue.',
};

/**
 * One-component labeling cheatsheet, used in two places:
 *  - Help page (rendered inline)
 *  - Triage / Issues / Agent Health pages (rendered inside a Modal via the
 *    LabelingGuideButton wrapper below)
 *
 * Mirrors docs/OPS_LABELING_GUIDE.md content; that file stays the canonical
 * Slack-pasteable source, this is the in-app projection.
 */
export const LabelingGuide: React.FC = () => {
  const classRows = FLEET_CLASS_IDS.map((id) => {
    const meta = classTagMap[id];
    return {
      key: id,
      id,
      label: meta?.label ?? `cls ${id}`,
      color: meta?.color,
      name: classTooltipMap[id] ?? `class ${id}`,
      pickWhen: CLASS_PICK_WHEN[id] ?? '',
      cold: COLD_CLASS_IDS.has(id),
    };
  });

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert
        type="warning"
        showIcon
        message="Why this matters"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            The agent learns from <Text strong>your closes</Text>. Every issue
            closed with a class set writes a row that the agent retrieves at
            kNN time and uses to score itself. Until enough rows accumulate,
            every accuracy metric on{' '}
            <RouterLink to="/agent-health">Agent Health</RouterLink> is
            computed against the rule's own past outputs — self-validation,
            not truth. First milestone: <Text strong>50 operator labels</Text>.
            Phase-2 unlock: <Text strong>100 labels + hold-out ≥ 70%</Text>.
          </Paragraph>
        }
      />

      <Card size="small" title="What labeling means in practice">
        <Paragraph style={{ marginBottom: 8 }}>
          Closing an issue <Text strong>with a class set</Text> is the labeling event.
          Specifically:
        </Paragraph>
        <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
          <li>
            Open an issue on the{' '}
            <RouterLink to="/issues">Issues tab</RouterLink>.
          </li>
          <li>
            Use a triage action (numbered chips, hotkeys{' '}
            <Text code>1</Text>–<Text code>8</Text>) — or the dropdown — that
            sets a <Text code>ticket_class_id</Text>.
          </li>
          <li>
            The status flip to Done / Auto-Resolved / False Alert writes the
            outcome row.
          </li>
        </ol>
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          Bumping status without setting a class doesn't capture a label.
          Use the numbered chips — every action has a class baked in.
        </Paragraph>
      </Card>

      <Card size="small" title="What each class means">
        <Table
          size="small"
          dataSource={classRows}
          pagination={false}
          rowKey="key"
          columns={[
            {
              title: 'Class',
              key: 'class',
              width: 130,
              render: (_v, row) => (
                <Space size={4}>
                  <Tag color={row.color}>{row.label}</Tag>
                  {row.cold && <Tag color="purple">cold</Tag>}
                </Space>
              ),
            },
            {
              title: 'Name',
              dataIndex: 'name',
              width: 200,
              render: (n: string, row) => (
                <Text style={{ fontSize: 12 }}>
                  {n} <Text type="secondary">({row.id})</Text>
                </Text>
              ),
            },
            {
              title: 'Pick when',
              dataIndex: 'pickWhen',
              render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text>,
            },
          ]}
        />
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          <Tag color="purple">cold</Tag> = rule never picks this on its own.
          Labels here are worth <Text strong>3×</Text> the others — they fill
          gaps the rule can't.
        </Paragraph>
      </Card>

      <Card size="small" title="Where to start">
        <Paragraph style={{ marginBottom: 8 }}>
          Open <RouterLink to="/agent-health">Agent Health</RouterLink> →
          scroll to <Text strong>"What to triage next"</Text> (the Active
          Learning Queue). The <Text strong>Why</Text> column tells you why
          each row is worth your time:
        </Paragraph>
        <ul style={{ paddingLeft: 20, marginBottom: 8 }}>
          <li>
            <Tag color="orange">class-10 lock</Tag> rule punted to "unknown",
            agent has an opinion. Your label resolves the ambiguity.
          </li>
          <li>
            <Tag color="gold">disagrees</Tag> agent picked a different class
            than the rule. Your label decides who's right.
          </li>
          <li>
            <Tag>low confidence</Tag> fallback; agent isn't sure of anything.
          </li>
        </ul>
        <Paragraph style={{ marginBottom: 0 }}>
          Click <Text strong>Triage</Text> on any row → it deep-links to the
          Issues tab with that issue selected. Pick a class. Close.
        </Paragraph>
      </Card>

      <Card size="small" title="Anti-patterns to avoid">
        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
          <li>
            <Text strong>Don't accept the rule's suggestion if you
            disagree.</Text>{' '}
            One click locks in the wrong label. Use the dropdown.
          </li>
          <li>
            <Text strong>Don't default to class 10 to save time.</Text> Class
            10 is the honest "we don't know" bucket — it gives the agent zero
            learning. Pick a real class even if it's a guess.
          </li>
          <li>
            <Text strong>Don't ignore the agent's "disagrees" tag.</Text>{' '}
            That's it asking you to look. Read its reasoning before deciding.
          </li>
          <li>
            <Text strong>Don't bulk-close without classes.</Text> Bulk-close
            skips the class step → no label → no training.
          </li>
        </ul>
      </Card>

      <Card size="small" title="Quick reference">
        <Table
          size="small"
          showHeader={false}
          pagination={false}
          dataSource={[
            { k: 'Where to label', v: <RouterLink to="/issues">/issues</RouterLink> },
            {
              k: 'Where to find high-value issues',
              v: (
                <>
                  <RouterLink to="/agent-health">/agent-health</RouterLink> →
                  "What to triage next"
                </>
              ),
            },
            {
              k: 'Where to see your contribution',
              v: (
                <>
                  <RouterLink to="/agent-health">/agent-health</RouterLink> →
                  contribution banner
                </>
              ),
            },
            {
              k: 'DB landing',
              v: (
                <Text code style={{ fontSize: 12 }}>
                  sf_support.issue_outcomes (decision_source='operator')
                </Text>
              ),
            },
            { k: 'First milestone', v: '50 operator labels' },
            { k: 'Phase-2 unlock', v: '100 operator labels + hold-out ≥ 70%' },
            { k: 'Bug / doc report', v: '#fleet-ops Slack' },
          ]}
          rowKey="k"
          columns={[
            { dataIndex: 'k', width: 220, render: (k: string) => <Text strong style={{ fontSize: 12 }}>{k}</Text> },
            { dataIndex: 'v', render: (v: React.ReactNode) => <span style={{ fontSize: 12 }}>{v}</span> },
          ]}
        />
      </Card>

      <Title level={5} style={{ marginBottom: 0 }}>Also available</Title>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Slack-pasteable Markdown source:{' '}
        <Typography.Link
          href="https://github.com/SensorflowOrg/fleet-ticket-triage/blob/main/docs/OPS_LABELING_GUIDE.md"
          target="_blank"
          rel="noreferrer"
        >
          docs/OPS_LABELING_GUIDE.md ↗
        </Typography.Link>
      </Paragraph>
    </Space>
  );
};

// ----------------------------------------------------------------------------
// Modal trigger wrapper. Use as <LabelingGuideButton size="small">Label guide</LabelingGuideButton>
// or pass a children render-prop for a custom trigger.
// ----------------------------------------------------------------------------

import { Button, Modal } from 'antd';
import { ReadOutlined } from '@ant-design/icons';
import { useState } from 'react';

interface LabelingGuideButtonProps {
  /** Override the default trigger button. */
  children?: React.ReactNode;
  /** Default button size when no custom trigger is supplied. */
  size?: 'small' | 'middle' | 'large';
  /** Default button text when no custom trigger is supplied. */
  label?: string;
}

export const LabelingGuideButton: React.FC<LabelingGuideButtonProps> = ({
  children,
  size = 'small',
  label = 'Labeling guide',
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {children ? (
        <span onClick={() => setOpen(true)} style={{ cursor: 'pointer' }}>
          {children}
        </span>
      ) : (
        <Button
          size={size}
          icon={<ReadOutlined />}
          onClick={() => setOpen(true)}
          aria-label="Open labeling guide"
        >
          {label}
        </Button>
      )}
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        title="Labeling guide"
        width={780}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        <LabelingGuide />
      </Modal>
    </>
  );
};
